import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import * as LucideIcons from "lucide-react";
import { ChevronDown, Search } from "lucide-react";

/**
 * Curated set of lucide-react icons relevant to pharma / life-sciences / recruitment.
 * Grouped so admins can visually browse.
 */
const CURATED_ICONS = [
  // Pharma & Science
  "FlaskConical", "Microscope", "Pill", "Stethoscope", "Syringe", "TestTube", "Beaker", "Dna",
  "HeartPulse", "Hospital", "Cross", "Activity",
  // Manufacturing / QA
  "Factory", "Building2", "Warehouse", "Cog", "Wrench", "Boxes", "Package", "Truck",
  "ShieldCheck", "BadgeCheck", "ClipboardCheck", "FileCheck",
  // Business / Recruitment
  "Briefcase", "Users", "UserPlus", "User", "UserCheck", "Award", "Trophy", "Crown",
  "Target", "TrendingUp", "BarChart3", "PieChart", "LineChart",
  // Sales / Marketing
  "Megaphone", "Send", "Mail", "Phone", "MessageCircle", "Globe",
  // Regulatory / Legal
  "Scale", "Gavel", "BookOpen", "FileText", "FileSignature",
  // Sparkles / Misc
  "Sparkles", "Zap", "Lightbulb", "Compass", "Eye", "HeartHandshake", "Handshake",
];

export default function IconPicker({ value, onChange, testId = "icon-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return CURATED_ICONS;
    const s = q.toLowerCase();
    return CURATED_ICONS.filter((n) => n.toLowerCase().includes(s));
  }, [q]);

  const Current = (value && LucideIcons[value]) || LucideIcons.Building2;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start gap-2"
          data-testid={testId}
        >
          <Current className="h-4 w-4" />
          <span className="flex-1 truncate text-left text-sm">{value || "Choose icon"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-3" align="start">
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search icons…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-8 pl-7 text-sm"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="grid max-h-72 grid-cols-6 gap-1 overflow-auto pr-1">
          {filtered.map((name) => {
            const I = LucideIcons[name];
            if (!I) return null;
            const active = value === name;
            return (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => { onChange(name); setOpen(false); }}
                data-testid={`${testId}-opt-${name}`}
                className={[
                  "grid h-10 place-items-center rounded-md border transition-colors",
                  active ? "border-primary bg-primary/10 text-primary" : "border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50",
                ].join(" ")}
              >
                <I className="h-4 w-4" />
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-6 py-6 text-center text-xs text-slate-400">No icons match &ldquo;{q}&rdquo;</div>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between border-t pt-2 text-[11px] text-slate-500">
          <span>Selected: <code>{value || "—"}</code></span>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-700"
            onClick={() => { onChange(""); setOpen(false); }}
          >
            Clear
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
